import { ArrowRight, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type {
	AliasRow,
	RecommendationStatus,
} from "../../types/recommendations";

export const AliasesTab = () => {
	const [rows, setRows] = useState<AliasRow[]>([]);
	const [alias, setAlias] = useState("");
	const [canonical, setCanonical] = useState("");
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setRows(await fetchJson<AliasRow[]>("/recommendations/aliases"));
	}, []);

	useEffect(() => {
		load().catch((e) => setError(String(e)));
	}, [load]);

	const add = useCallback(async () => {
		setError(null);
		try {
			await sendJson("/recommendations/aliases", "POST", {
				alias,
				canonicalQuery: canonical,
				status: "approved",
			});
			setAlias("");
			setCanonical("");
			await load();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Could not save alias");
		}
	}, [alias, canonical, load]);

	return (
		<div className="space-y-6">
			<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
				<h3 className="text-sm font-semibold text-slate-700">
					Query normalization
				</h3>
				<p className="mt-1 text-[13px] text-slate-500">
					Map a misspelling or variant onto the query it was curated under.
					Applied on lookup when the typed query has no curated entry of its
					own.
				</p>

				<div className="mt-4 flex flex-wrap items-center gap-2">
					<input
						value={alias}
						onChange={(e) => setAlias(e.target.value)}
						placeholder="typed query, e.g. ifone 16"
						className="min-w-48 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
					/>
					<ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
					<input
						value={canonical}
						onChange={(e) => setCanonical(e.target.value)}
						placeholder="curated query, e.g. iphone 16"
						className="min-w-48 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
					/>
					<button
						type="button"
						onClick={add}
						disabled={!alias.trim() || !canonical.trim()}
						className="flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover px-3 py-2 text-[13px] font-semibold text-black disabled:opacity-50"
					>
						<Plus className="h-4 w-4" />
						Add
					</button>
				</div>

				{error && (
					<p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-600">
						{error}
					</p>
				)}
			</div>

			<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
				<h3 className="mb-4 text-sm font-semibold text-slate-700">Aliases</h3>
				<div className="overflow-x-auto">
					<table className="w-full text-left text-[13px]">
						<thead>
							<tr className="border-b border-slate-200 text-slate-500">
								<th className="pb-2 font-medium">Typed</th>
								<th className="pb-2 font-medium">Resolves to</th>
								<th className="pb-2 font-medium">Source</th>
								<th className="pb-2 font-medium">Status</th>
								<th className="pb-2 font-medium" />
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr>
									<td colSpan={5} className="py-3 text-slate-400">
										No aliases yet. The scheduled job proposes these for
										searches that return nothing.
									</td>
								</tr>
							)}
							{rows.map((r) => (
								<tr
									key={r.id}
									className="border-b border-slate-100 last:border-0"
								>
									<td className="py-2 pr-2 font-medium text-slate-700">
										{r.alias}
									</td>
									<td className="py-2 pr-2 text-slate-600">
										{r.canonicalQuery}
									</td>
									<td className="py-2 pr-2 text-slate-500">{r.source}</td>
									<td className="py-2 pr-2">
										<select
											value={r.status}
											onChange={async (e) => {
												await sendJson(
													`/recommendations/aliases/${r.id}/status`,
													"PATCH",
													{ status: e.target.value as RecommendationStatus },
												);
												await load();
											}}
											aria-label={`Status for ${r.alias}`}
											className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 outline-none"
										>
											<option value="draft">draft</option>
											<option value="approved">approved</option>
											<option value="published">published</option>
										</select>
									</td>
									<td className="py-2 text-right">
										<button
											type="button"
											aria-label={`Delete alias ${r.alias}`}
											onClick={async () => {
												await sendJson(
													`/recommendations/aliases/${r.id}`,
													"DELETE",
												);
												await load();
											}}
											className="text-slate-400 hover:text-rose-500"
										>
											<Trash2 className="h-4 w-4" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};
